import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
// import { User } from '@models/user';
import { User } from '@models/user';
import { SessionService } from './session.service';
import { CacheService } from './cache.service';
import { MessageService } from 'primeng/api';
import { APP_SHELL_IS_MOBILE } from '../tokens/app-shell.token';
import { MobileErrorCategory, MobileErrorUxService } from './mobile-error-ux.service';

type HttpErrorHandlingOptions = {
    suppressGlobalNotification?: boolean;
};

type NormalizedHttpError = {
    message: string;
    summary: string;
    status: number;
    category: MobileErrorCategory;
    key: string;
};

@Injectable({
    providedIn: 'root'
})
export class HttpService {
    private user: User;
    private userLoggedIn: string= "";
    private readonly isMobileShell = inject(APP_SHELL_IS_MOBILE, { optional: true }) ?? false;
    constructor(
        private http: HttpClient,
        private sessionSvc: SessionService,
        private cache:CacheService,
        private messageSvc: MessageService,
        private readonly mobileErrorUx: MobileErrorUxService
    ){ }

    public setHeaders(additionalHeaders: HttpHeaders = new HttpHeaders()): HttpHeaders {
        let headers = new HttpHeaders();

        /** Get Access token from authentication service */
        this.user = this.sessionSvc.getUserValue();
        this.userLoggedIn = <string>(this.user && this.user.accessToken);

        if (this.userLoggedIn) {
            headers = headers.append('Authorization', 'Bearer ' + this.user.accessToken);
        }

        // if(additionalHeaders && !additionalHeaders.get('Content-Type')){
        //     headers = headers.append('Content-Type', 'application/json');
        // }
        return headers;
    }

    public get<T>(
        url: string,
        params?: any,
        additionalHeaders?: HttpHeaders,
        responseType: any = "json",
        cacheOptions?: { enabled: boolean; key?: string; ttl?: number },
        errorOptions?: HttpErrorHandlingOptions
      ): Observable<T> {
      
        const headers = this.setHeaders(additionalHeaders);
        const key = cacheOptions?.key || url + '?' + new HttpParams({ fromObject: params }).toString();
        const ttl = cacheOptions?.ttl ?? 5 * 60 * 1000;
      
        const httpCall = () =>
          this.http.get<T>(url, { params, responseType, headers })
            .pipe(catchError((error) => this.handleError(error, errorOptions)));
      
        if (cacheOptions?.enabled) {
          return this.cache.getOrSet<T>(key, httpCall, ttl);
        } else {
          return httpCall();
        }
      }

    /** Emits the response body (same as HttpClient with default `observe: 'body'`). */
    public post<T>(url: string, data: any, additionalHeaders?: HttpHeaders, errorOptions?: HttpErrorHandlingOptions): Observable<T> {
        return this.http.post<T>(url, data, { headers: this.setHeaders(additionalHeaders), observe: 'body' })
            .pipe(
                catchError((error) => this.handleError(error, errorOptions))
            );
    }

    public put<T>(url: string, data: any, additionalHeaders?: HttpHeaders, errorOptions?: HttpErrorHandlingOptions): Observable<T> {
        return this.http.put<T>(url, data, { headers: this.setHeaders(additionalHeaders) })
            .pipe(
                catchError((error) => this.handleError(error, errorOptions))
            );
    }

    public delete<T>(url: string, data?: any, additionalHeaders?: HttpHeaders, errorOptions?: HttpErrorHandlingOptions): Observable<T> {
        return this.http.delete<T>(url, { headers: this.setHeaders(additionalHeaders), body: data })
            .pipe(
                catchError((error) => this.handleError(error, errorOptions))
            );
    }

    public patch<T>(url: string, data: any, additionalHeaders?: HttpHeaders, params: HttpParams = new HttpParams(), responseType: any = "json", errorOptions?: HttpErrorHandlingOptions): Observable<T> {
        return this.http.patch<T>(url, data, { responseType: responseType, headers: this.setHeaders(additionalHeaders), params })
            .pipe(
                catchError((error) => this.handleError(error, errorOptions))
            );
    }

    private handleError(error: HttpErrorResponse, options?: HttpErrorHandlingOptions) {
        const normalized = this.normalizeHttpError(error);
        console.error(`HTTP ${normalized.status} (${normalized.category})`, error.error ?? error.message);

        if (!options?.suppressGlobalNotification && this.shouldGloballyNotify(normalized)) {
            this.notifyUser(normalized);
        }

        return throwError(() => new Error(normalized.message));
    }

    private shouldGloballyNotify(error: NormalizedHttpError): boolean {
        return error.category === 'network' || error.category === 'server' || error.category === 'auth';
    }

    private notifyUser(error: NormalizedHttpError): void {
        if (this.isMobileShell) {
            this.mobileErrorUx.notify({
                key: error.key,
                message: error.message,
                status: error.status,
                category: error.category,
                severity: error.category === 'auth' ? 'warn' : 'error',
            });
            return;
        }

        this.messageSvc?.add({
            severity: error.category === 'auth' ? 'warn' : 'error',
            summary: error.summary,
            detail: error.message,
        });
    }

    private normalizeHttpError(error: HttpErrorResponse): NormalizedHttpError {
        const status = Number(error.status ?? 0);
        const fallbackMessage = 'Something went wrong; please try again later.';
        let category: MobileErrorCategory = 'unknown';
        let message = fallbackMessage;

        if (error.error instanceof ErrorEvent || status === 0) {
            category = 'network';
            message = 'Network issue. Please check your connection and try again.';
        } else if (status === 401 || status === 403) {
            category = 'auth';
            message = 'Your session is restricted or expired. Please sign in again.';
        } else if (status >= 500) {
            category = 'server';
            message = 'Server error occurred. Please try again shortly.';
        } else if (status >= 400) {
            category = 'client';
            message = this.extractClientErrorMessage(error) ?? fallbackMessage;
        }

        return {
            status,
            category,
            message,
            summary: category === 'auth' ? 'Session' : 'Error',
            key: `${category}:${status}:${message}`,
        };
    }

    private extractClientErrorMessage(error: HttpErrorResponse): string | null {
        const maybeMessage = error.error?.message;
        if (typeof maybeMessage === 'string' && maybeMessage.trim()) {
            return maybeMessage.trim();
        }
        const maybeError = error.error?.error;
        if (typeof maybeError === 'string' && maybeError.trim()) {
            return maybeError.trim();
        }
        if (typeof error.statusText === 'string' && error.statusText.trim()) {
            return error.statusText.trim();
        }
        return null;
    }
}